import { Skeleton } from '@mantine/core'

import type { LifecycleBreakdown } from '../mockData'

import { formatDays } from './chartTheme'

// ---------------------------------------------------------------------------
// Phase row — compact layout for 3-col view
// ---------------------------------------------------------------------------

interface PhaseRowProps {
  label: string
  avg: number
  median: number
  maxTotal: number
  color: string
}

const PhaseRow = ({ label, avg, median, maxTotal, color }: PhaseRowProps) => {
  const widthPct = maxTotal > 0 ? (avg / maxTotal) * 100 : 0

  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium text-gray-600">{label}</span>
        <div className="flex items-baseline gap-1">
          <span className="text-sm font-semibold text-gray-800">
            {formatDays(avg)}
          </span>
          <span className="text-xs text-gray-400">avg</span>
        </div>
      </div>
      <div className="h-5 w-full rounded bg-gray-100">
        <div
          className="flex h-full items-center rounded"
          style={{
            width: avg > 0 ? `${Math.max(widthPct, 6)}%` : '0%',
            backgroundColor: color,
          }}
        />
      </div>
      <span className="text-[11px] text-gray-400">
        median {formatDays(median)}
      </span>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export interface VoELifecycleChartProps {
  data: LifecycleBreakdown | null
  loading?: boolean
}

export const VoELifecycleChart = ({
  data,
  loading = false,
}: VoELifecycleChartProps) => {
  if (loading) {
    return (
      <div className="flex flex-col gap-4">
        <Skeleton height={50} />
        <Skeleton height={50} />
        <Skeleton height={50} />
      </div>
    )
  }

  if (!data) {
    return (
      <div className="flex h-[160px] items-center justify-center text-sm text-gray-400">
        No data available
      </div>
    )
  }

  const maxTotal = data.avgTotalDays ?? 0

  return (
    <div className="flex flex-col gap-4">
      <PhaseRow
        label="Wait"
        avg={data.avgWaitDays ?? 0}
        median={data.medianWaitDays ?? 0}
        maxTotal={maxTotal}
        color="#98A2B3"
      />
      <PhaseRow
        label="Active"
        avg={data.avgActiveDays ?? 0}
        median={data.medianActiveDays ?? 0}
        maxTotal={maxTotal}
        color="#1C93D7"
      />
      <PhaseRow
        label="Total"
        avg={data.avgTotalDays ?? 0}
        median={data.medianTotalDays ?? 0}
        maxTotal={maxTotal}
        color="#344054"
      />
    </div>
  )
}
